import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { FileSignature, ClipboardList, Users, Lock, Cloud, Check, ArrowRight } from "lucide-react";
import { useApp } from "@/context/AppProviders";
import { PORTAL_URL, PORTAL_LOGIN_URL } from "@/config";

const featureIcons = [FileSignature, ClipboardList, Users, Lock];

const track = (event) => window.posthog?.capture?.(event);

const KlantPortaal = () => {
  const { t } = useApp();
  const p = t.portal;
  const c = p.card;

  return (
    <section
      id="klantportaal"
      data-testid="portal-section"
      className="py-24 sm:py-32 border-t border-border"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.5 }}
          className="max-w-3xl"
        >
          <p className="inline-flex items-center gap-2 font-mono text-sm uppercase tracking-widest text-blue-500">
            <Cloud className="h-4 w-4" strokeWidth={2} />
            {p.eyebrow}
          </p>
          <h2 className="font-heading mt-3 text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight">
            {p.title}
          </h2>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground">
            {p.lead.split("portaal.securemaintpro.nl")[0]}
            <a
              href={PORTAL_URL}
              className="text-blue-500 underline-offset-2 hover:underline"
              data-testid="portal-lead-link"
            >
              portaal.securemaintpro.nl
            </a>
            {p.lead.split("portaal.securemaintpro.nl")[1]}
          </p>
        </motion.div>

        <div className="mt-12 grid gap-10 lg:grid-cols-[1.1fr_0.9fr] lg:items-start">
          {/* Feature grid */}
          <div className="grid gap-4 sm:grid-cols-2">
            {p.features.map((f, i) => {
              const Icon = featureIcons[i];
              return (
                <motion.div
                  key={f.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.4, delay: (i % 2) * 0.08 }}
                  data-testid={`portal-feature-${i}`}
                  className="rounded-sm border border-border bg-card p-5"
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-sm border border-blue-600/30 bg-blue-600/10 text-blue-500">
                    <Icon className="h-5 w-5" strokeWidth={2} />
                  </div>
                  <h3 className="font-heading mt-4 text-base font-medium tracking-tight">
                    {f.title}
                  </h3>
                  <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                    {f.desc}
                  </p>
                </motion.div>
              );
            })}
          </div>

          {/* Pricing card */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.5 }}
            data-testid="portal-pricing-card"
            className="rounded-sm border border-blue-600/40 bg-card p-8 shadow-[0_0_0_1px_rgb(37,99,235),0_0_40px_-12px_rgb(37,99,235)]"
          >
            <p className="font-mono text-xs uppercase tracking-widest text-blue-500">
              {c.eyebrow}
            </p>
            <h3 className="font-heading mt-2 text-lg font-bold tracking-tight">
              {c.title}
            </h3>
            <div className="mt-5 flex items-baseline gap-1.5 border-y border-border py-5">
              <span
                data-testid="portal-price"
                className="font-heading text-5xl font-bold tracking-tight text-blue-500"
              >
                {c.price}
              </span>
              <span className="font-mono text-sm text-muted-foreground">
                {c.period}
              </span>
            </div>
            <p className="mt-1.5 text-xs text-muted-foreground/70" data-testid="portal-price-vat">
              {t.shop.vat}
            </p>
            <p className="mt-3 text-sm text-muted-foreground">{c.year}</p>

            <ul className="mt-6 space-y-3">
              {c.includes.map((item) => (
                <li key={item} className="flex items-start gap-3 text-sm">
                  <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-blue-500" />
                  <span className="text-muted-foreground">{item}</span>
                </li>
              ))}
            </ul>

            <p className="mt-5 rounded-sm border border-border bg-secondary/40 p-3 text-xs leading-relaxed text-muted-foreground">
              {c.eligibility}
            </p>

            <div className="mt-6 flex flex-col gap-3">
              <Link
                to="/checkout?plan=portal"
                onClick={() => track("Portal Request Access")}
                data-testid="cta-request-portal"
                className="inline-flex h-11 items-center justify-center rounded-sm bg-blue-600 px-5 text-sm font-medium text-white transition-colors duration-200 hover:bg-blue-500"
              >
                {c.ctaRequest}
              </Link>
              <a
                href={PORTAL_LOGIN_URL}
                onClick={() => track("Portal Login Click")}
                data-testid="cta-portal-login"
                className="inline-flex h-11 items-center justify-center gap-1.5 rounded-sm border border-border bg-secondary/50 px-5 text-sm font-medium text-foreground transition-colors duration-200 hover:bg-secondary"
              >
                {c.ctaLogin}
                <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default KlantPortaal;
